import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, Eye, EyeOff, ArrowLeft, MailCheck } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const field = "w-full bg-white border border-[var(--mf-line)] px-4 py-3 focus:border-[var(--mf-red)] transition-colors";

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin: window.location.origin });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between bg-[var(--mf-ink)] text-white p-12 mf-grain relative overflow-hidden">
        <div className="relative font-head font-bold text-2xl tracking-tight">MF<span className="text-[var(--mf-red)]"> · </span>MED</div>
        <div className="relative">
          <div className="w-8 h-[2px] bg-[var(--mf-red)]" />
          <h2 className="font-head font-light text-3xl mt-6 leading-tight">Verwaltung<br /><span className="font-semibold">MF MED GmbH</span></h2>
          <p className="text-white/50 mt-4 max-w-sm">Inhalte, Leistungen, Zielgruppen, Rechtstexte und Kontaktanfragen bearbeiten.</p>
        </div>
        <span className="text-white/30 text-xs relative">… für Sie unterwegs …</span>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 font-head font-bold text-2xl">MF<span className="text-[var(--mf-red)]"> · </span>MED</div>

          {mode === "login" && (
            <form onSubmit={submit} data-testid="login-form">
              <span className="mf-eyebrow">Anmeldung</span>
              <h1 className="font-head font-bold text-3xl tracking-tight mt-5 text-[var(--mf-ink)]">Willkommen zurück</h1>
              <p className="text-[var(--mf-grey)] mt-2 text-sm">Bitte melden Sie sich an, um fortzufahren.</p>
              {error && <div data-testid="login-error" className="mt-6 text-sm text-[var(--mf-red)] border border-[var(--mf-red)]/30 bg-[var(--mf-red)]/5 px-4 py-3">{error}</div>}
              <div className="mt-6 space-y-4">
                <input data-testid="login-email" type="email" required className={field} placeholder="E-Mail" value={email} onChange={(e) => setEmail(e.target.value)} />
                <div className="relative">
                  <input data-testid="login-password" type={showPw ? "text" : "password"} required className={field + " pr-12"} placeholder="Passwort" value={password} onChange={(e) => setPassword(e.target.value)} />
                  <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="toggle-password"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--mf-grey)] hover:text-[var(--mf-ink)]" aria-label="Passwort anzeigen">
                    {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <button data-testid="login-submit" disabled={loading} className="mf-cta w-full disabled:opacity-60">
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <Lock size={16} />} {loading ? "Anmelden …" : "Anmelden"}
                </button>
                <button type="button" data-testid="forgot-link" onClick={() => { setMode("forgot"); setError(""); }} className="text-sm text-[var(--mf-grey)] hover:text-[var(--mf-red)] w-full text-center">
                  Passwort vergessen?
                </button>
              </div>
            </form>
          )}

          {mode === "forgot" && (
            <form onSubmit={submitForgot} data-testid="forgot-form">
              <span className="mf-eyebrow">Passwort vergessen</span>
              <h1 className="font-head font-bold text-3xl tracking-tight mt-5 text-[var(--mf-ink)]">Zurücksetzen</h1>
              {sent ? (
                <div data-testid="forgot-success" className="mt-6 border border-[var(--mf-line)] p-5 bg-[var(--mf-mist)]">
                  <MailCheck className="text-[var(--mf-red)]" />
                  <p className="mt-3 text-sm text-[var(--mf-ink)]">Falls ein Konto zu dieser E-Mail existiert, haben wir einen Link zum Zurücksetzen gesendet. Bitte prüfen Sie Ihr Postfach.</p>
                </div>
              ) : (
                <>
                  <p className="text-[var(--mf-grey)] mt-2 text-sm">Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Link zum Zurücksetzen.</p>
                  {error && <div className="mt-6 text-sm text-[var(--mf-red)] border border-[var(--mf-red)]/30 bg-[var(--mf-red)]/5 px-4 py-3">{error}</div>}
                  <div className="mt-6 space-y-4">
                    <input data-testid="forgot-email" type="email" required className={field} placeholder="E-Mail" value={email} onChange={(e) => setEmail(e.target.value)} />
                    <button data-testid="forgot-submit" disabled={loading} className="mf-cta w-full disabled:opacity-60">
                      {loading ? <Loader2 size={18} className="animate-spin" /> : null} Link senden
                    </button>
                  </div>
                </>
              )}
              <button type="button" onClick={() => { setMode("login"); setSent(false); setError(""); }} className="mt-5 text-sm text-[var(--mf-grey)] hover:text-[var(--mf-red)] inline-flex items-center gap-1">
                <ArrowLeft size={14} /> Zurück zur Anmeldung
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
